import axios from "axios";

export const LOCAL = "151b1bf60f3e4026814183ffe9c75148";

// Mismo API en dos alojamientos distintos: primero el .es, si falla el ddns
const PRIMARY = "https://booking-api.es/api/v1";
const FALLBACK = "https://booking-api.ddns.net/api/v1";

const TIMEOUT_MS = 10000;
const HEALTH_TIMEOUT_MS = 5000;

let currentBase = PRIMARY;
try {
  const saved = sessionStorage.getItem("api_base");
  if (saved === PRIMARY || saved === FALLBACK) currentBase = saved;
} catch (e) {
  // sessionStorage puede no estar disponible (modo privado antiguo)
}

const otherBase = (base) => (base === PRIMARY ? FALLBACK : PRIMARY);

const rememberBase = (base) => {
  currentBase = base;
  try {
    sessionStorage.setItem("api_base", base);
  } catch (e) {
    // ignorar
  }
};

export const getApiBase = () => currentBase;

// Fallo de infraestructura: no hay respuesta (red caída, DNS bloqueado, timeout)
// o el servidor no está operativo. Un 4xx es un rechazo de negocio: NO se reintenta
// contra el otro host porque devolvería lo mismo.
const isInfraError = (error, { retryOn500 = false } = {}) =>
  !error.response ||
  [502, 503, 504].includes(error.response.status) ||
  (retryOn500 && error.response.status === 500);

const describeError = (error) => ({
  message: error?.message || String(error),
  code: error?.code || null,
  status: error?.response?.status ?? null,
  response_data: error?.response?.data ?? null,
});

// Envía un reporte a /report/fallback con todo el contexto disponible.
// Nunca lanza: prueba la base activa y después la otra.
export const sendReport = async (payload) => {
  const body = {
    ...payload,
    timestamp: new Date().toISOString(),
    page: typeof window !== "undefined" ? window.location.href : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    connection:
      typeof navigator !== "undefined"
        ? navigator.connection?.effectiveType ?? null
        : null,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    screen:
      typeof window !== "undefined"
        ? `${window.screen?.width}x${window.screen?.height}`
        : null,
  };
  const bases = [currentBase, otherBase(currentBase)];
  for (const base of bases) {
    try {
      await axios.post(`${base}/report/fallback`, body, {
        timeout: HEALTH_TIMEOUT_MS,
        headers: { "Content-Type": "application/json" },
      });
      return;
    } catch (e) {
      // probar la siguiente base
    }
  }
};

// Comprueba una vez por sesión qué base responde, empezando SIEMPRE por el .es
// (así se vuelve al primario automáticamente cuando se recupera).
let resolvePromise = null;
export const resolveApiBase = () => {
  if (resolvePromise) return resolvePromise;
  resolvePromise = (async () => {
    try {
      await axios.get(`${PRIMARY}/close/local/${LOCAL}`, {
        timeout: HEALTH_TIMEOUT_MS,
      });
      rememberBase(PRIMARY);
    } catch (primaryError) {
      try {
        await axios.get(`${FALLBACK}/close/local/${LOCAL}`, {
          timeout: HEALTH_TIMEOUT_MS,
        });
        rememberBase(FALLBACK);
        sendReport({
          type: "primary_down_on_load",
          failed_base: PRIMARY,
          active_base: FALLBACK,
          error: describeError(primaryError),
        });
      } catch (fallbackError) {
        sendReport({
          type: "no_api_reachable_on_load",
          primary_error: describeError(primaryError),
          fallback_error: describeError(fallbackError),
        });
      }
    }
    return currentBase;
  })();
  return resolvePromise;
};

// Petición con failover automático: intenta la base activa y, si el fallo es de
// infraestructura, reintenta una vez contra la otra base y reporta el incidente.
export const apiRequest = async ({
  method = "get",
  path,
  data,
  params,
  context,
  retryOn500 = false,
}) => {
  await resolveApiBase();
  const run = (base) =>
    axios({
      method,
      url: `${base}${path}`,
      data,
      params,
      timeout: TIMEOUT_MS,
      headers: { "Content-Type": "application/json" },
    });

  const base = currentBase;
  try {
    return await run(base);
  } catch (firstError) {
    if (!isInfraError(firstError, { retryOn500 })) {
      sendReport({
        type: "api_error",
        context,
        api_base: base,
        method,
        path,
        error: describeError(firstError),
      });
      throw firstError;
    }
    const backup = otherBase(base);
    sendReport({
      type: "fallback_retry",
      context,
      failed_base: base,
      retry_base: backup,
      method,
      path,
      error: describeError(firstError),
    });
    try {
      const res = await run(backup);
      rememberBase(backup);
      return res;
    } catch (secondError) {
      sendReport({
        type: "both_bases_failed",
        context,
        method,
        path,
        first_base: base,
        first_error: describeError(firstError),
        second_base: backup,
        second_error: describeError(secondError),
      });
      secondError.isNetwork = isInfraError(secondError, { retryOn500 });
      throw secondError;
    }
  }
};

export const apiGet = (path, opts = {}) =>
  apiRequest({ method: "get", path, ...opts });
export const apiPost = (path, data, opts = {}) =>
  apiRequest({ method: "post", path, data, ...opts });
export const apiDelete = (path, opts = {}) =>
  apiRequest({ method: "delete", path, ...opts });
